import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loader, EmptyState } from "@/components/kit";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { LifeBuoy, Search, List, LayoutGrid, GripVertical } from "lucide-react";

const TS = { New: "bg-blue-100 text-blue-700", Assigned: "bg-indigo-100 text-indigo-700", "In Progress": "bg-amber-100 text-amber-700", "On Hold": "bg-slate-200 text-slate-600", Resolved: "bg-emerald-100 text-emerald-700", Closed: "bg-slate-200 text-slate-500", Reopened: "bg-rose-100 text-rose-700" };
const COL_DOT = { New: "bg-blue-500", Assigned: "bg-indigo-500", "In Progress": "bg-amber-500", "On Hold": "bg-slate-400", Reopened: "bg-rose-500", Resolved: "bg-emerald-500", Closed: "bg-slate-400" };
const PRIO = { Critical: "bg-rose-100 text-rose-700", High: "bg-orange-100 text-orange-700", Medium: "bg-amber-100 text-amber-700", Low: "bg-slate-100 text-slate-600" };
export const Pill = ({ s }) => <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TS[s] || "bg-slate-100 text-slate-600"}`}>{s}</span>;
const SLA = { overdue: "text-rose-600", due_soon: "text-amber-600", on_track: "text-emerald-600", met: "text-slate-400" };
const DEPARTMENTS = ["IT", "Electrical", "HVAC", "Housekeeping", "Plumbing", "Security", "Furniture", "General"];
const STATUSES = ["New", "Assigned", "In Progress", "On Hold", "Resolved", "Closed", "Reopened"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const BOARD_COLUMNS = ["New", "Assigned", "In Progress", "On Hold", "Reopened", "Resolved", "Closed"];
const DROP_TARGETS = ["Assigned", "In Progress", "On Hold", "Resolved"]; // statuses settable via the status endpoint

const KPI = ({ label, value, tone }) => (
  <div className="bg-white rounded-xl border border-slate-200 px-4 py-3" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
    <p className="text-2xl font-bold text-navy-900">{value ?? 0}</p>
    <p className={`text-xs font-medium ${tone || "text-slate-500"}`}>{label}</p>
  </div>
);

const TicketCard = ({ t, onOpen, draggable, onDragStart }) => (
  <div
    draggable={draggable}
    onDragStart={onDragStart}
    onClick={() => onOpen(t.id)}
    data-testid={`ticket-card-${t.ticket_number}`}
    className="bg-white rounded-xl border border-slate-200 p-3 cursor-pointer hover:border-navy-300 hover:shadow-md transition-all group"
  >
    <div className="flex items-start justify-between gap-2">
      <span className="font-mono text-[11px] font-semibold text-slate-400">{t.ticket_number}</span>
      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${PRIO[t.priority] || "bg-slate-100 text-slate-600"}`}>{t.priority}</span>
    </div>
    <p className="text-sm font-semibold text-navy-900 mt-1 leading-snug">{t.issue}</p>
    <p className="text-xs text-slate-400">{t.category} · {t.department}</p>
    <div className="mt-2 flex items-center justify-between text-xs">
      <span className="text-slate-500 truncate">{t.requester?.cabin || "—"}</span>
      <span className={`font-semibold ${SLA[t.sla?.state]}`}>{t.sla?.label}</span>
    </div>
    <div className="mt-2 flex items-center justify-between">
      <span className="text-xs text-slate-500 truncate">{t.assigned_user_name || <span className="text-indigo-500">Unassigned</span>}</span>
      {draggable && <GripVertical size={14} className="text-slate-300 group-hover:text-slate-400" />}
    </div>
  </div>
);

export default function Tickets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [list, setList] = useState(null);
  const [f, setF] = useState({});
  const [view, setView] = useState(() => localStorage.getItem("sf_ticket_view") || "list");
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const isSupport = user.role === "SUPPORT_TEAM";
  const isHead = user.role === "SERVICE_HEAD";
  const title = isSupport ? "My Tickets" : isHead ? "Service Head Dashboard" : "Ticketing";

  const setViewMode = (v) => { setView(v); localStorage.setItem("sf_ticket_view", v); };
  const qs = () => { const p = new URLSearchParams(); Object.entries(f).forEach(([k, v]) => { if (v) p.set(k, v); }); return p.toString(); };
  const load = () => {
    api.get("/tickets/stats").then((r) => setStats(r.data)).catch((e) => toast.error(apiError(e)));
    api.get(`/tickets?${qs()}`).then((r) => setList(r.data)).catch((e) => toast.error(apiError(e)));
  };
  useEffect(() => { load(); }, [f.status, f.priority, f.department, f.sla, f.assigned]);

  const openTicket = (id) => navigate(`/tickets/${id}`);

  const onDrop = async (status) => {
    setDragOver(null);
    const id = dragId; setDragId(null);
    if (!id || !DROP_TARGETS.includes(status)) return;
    const ticket = (list || []).find((t) => t.id === id);
    if (!ticket || ticket.status === status) return;
    // optimistic update
    setList((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
    try {
      await api.post(`/tickets/${id}/status`, { status });
      toast.success(`Moved to ${status}`);
      load();
    } catch (e) { toast.error(apiError(e)); load(); }
  };

  const viewToggle = (
    <div className="flex rounded-lg border border-slate-200 overflow-hidden">
      <button onClick={() => setViewMode("list")} data-testid="ticket-view-list" className={`px-2.5 py-2 transition-colors ${view === "list" ? "bg-navy-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} title="List view"><List size={16} /></button>
      <button onClick={() => setViewMode("board")} data-testid="ticket-view-board" className={`px-2.5 py-2 transition-colors ${view === "board" ? "bg-navy-900 text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`} title="Kanban board"><LayoutGrid size={16} /></button>
    </div>
  );

  const grouped = BOARD_COLUMNS.reduce((acc, s) => { acc[s] = (list || []).filter((t) => t.status === s); return acc; }, {});

  return (
    <div>
      <PageHeader title={title} subtitle="Track, assign and resolve service requests." actions={viewToggle} />

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-5 sf-fade-up">
          <KPI label="Total" value={stats.total} />
          <KPI label="New" value={stats.New} tone="text-blue-600" />
          <KPI label="Unassigned" value={stats.unassigned} tone="text-indigo-600" />
          <KPI label="In Progress" value={stats["In Progress"]} tone="text-amber-600" />
          <KPI label="Overdue" value={stats.overdue} tone="text-rose-600" />
          <KPI label="Resolved" value={stats.Resolved} tone="text-emerald-600" />
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 p-3 mb-4 flex flex-wrap items-center gap-2">
        <div className="relative grow min-w-[180px]">
          <Search size={15} className="absolute left-3 top-2.5 text-slate-400" />
          <Input className="pl-9 h-9" placeholder="Search ticket, cabin, requester…" data-testid="ticket-search" value={f.q || ""} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        {[["status", "Status", STATUSES], ["priority", "Priority", PRIORITIES], ["department", "Department", DEPARTMENTS], ["sla", "SLA", ["overdue", "due_soon", "on_track"]]].map(([k, ph, opts]) => (
          <Select key={k} value={f[k] || "__all"} onValueChange={(v) => setF({ ...f, [k]: v === "__all" ? "" : v })}>
            <SelectTrigger className="h-9 w-[140px]" data-testid={`filter-${k}`}><SelectValue placeholder={ph} /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All {ph}</SelectItem>{opts.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
          </Select>
        ))}
      </div>

      {!list ? <Loader /> : view === "board" ? (
        <div className="flex gap-3 overflow-x-auto pb-4 sf-fade-up" data-testid="ticket-board">
          {BOARD_COLUMNS.map((col) => (
            <div
              key={col}
              onDragOver={(e) => { if (dragId && DROP_TARGETS.includes(col)) { e.preventDefault(); setDragOver(col); } }}
              onDragLeave={() => setDragOver((c) => (c === col ? null : c))}
              onDrop={() => onDrop(col)}
              data-testid={`board-col-${col.replace(/ /g, "-")}`}
              className={`w-[260px] flex-shrink-0 rounded-2xl border p-2.5 transition-colors ${dragOver === col ? "border-navy-400 bg-navy-50/60" : "border-slate-200 bg-slate-50"}`}
            >
              <div className="flex items-center justify-between px-1.5 py-1 mb-2">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${COL_DOT[col]}`} />
                  <span className="text-sm font-semibold text-navy-900">{col}</span>
                </div>
                <span className="text-xs font-semibold text-slate-400 bg-white rounded-full px-2 py-0.5 border border-slate-200">{grouped[col].length}</span>
              </div>
              <div className="space-y-2 min-h-[60px]">
                {grouped[col].map((t) => (
                  <TicketCard key={t.id} t={t} onOpen={openTicket} draggable onDragStart={() => setDragId(t.id)} />
                ))}
                {grouped[col].length === 0 && <p className="text-xs text-slate-300 text-center py-4">No tickets</p>}
              </div>
            </div>
          ))}
        </div>
      ) : list.length === 0 ? <EmptyState icon={LifeBuoy} title="No tickets" subtitle="No service requests match your filters." /> : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-x-auto sf-fade-up">
          <Table>
            <TableHeader><TableRow className="bg-slate-50">
              <TableHead>Ticket</TableHead><TableHead>Issue</TableHead><TableHead>Cabin</TableHead>
              <TableHead>Priority</TableHead><TableHead>Dept</TableHead><TableHead>Assigned</TableHead>
              <TableHead>SLA</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {list.map((t) => (
                <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50" onClick={() => openTicket(t.id)} data-testid={`ticket-row-${t.ticket_number}`}>
                  <TableCell className="font-mono text-xs font-semibold text-navy-900">{t.ticket_number}</TableCell>
                  <TableCell className="text-navy-800">{t.issue}<div className="text-xs text-slate-400">{t.category}</div></TableCell>
                  <TableCell className="text-slate-500 text-sm">{t.requester?.cabin}<div className="text-xs text-slate-400">{t.requester?.company}</div></TableCell>
                  <TableCell className="text-sm">{t.priority}</TableCell>
                  <TableCell className="text-sm text-slate-500">{t.department}</TableCell>
                  <TableCell className="text-sm text-slate-500">{t.assigned_user_name || <span className="text-indigo-500">Unassigned</span>}</TableCell>
                  <TableCell className={`text-xs font-semibold ${SLA[t.sla?.state]}`}>{t.sla?.label}</TableCell>
                  <TableCell><Pill s={t.status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
